import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import FooterActions from '../../BpmnIoDiagram/FooterActions';
import { v4 as uuid } from 'uuid';

export interface Opportunity {
  id?: string;
  opportunity_title: string;
  opportunity_description: string;
  affected_process_stages: string[];
  expected_business_value: string[];
}

interface OpportunitiesStepProps {
  opportunities: Opportunity[];
  setOpportunities: React.Dispatch<React.SetStateAction<Opportunity[]>>;
  isMobile: boolean;
  isEditMode?: boolean;
  onSaveStateChange?: (enabled: boolean) => void;
  onSave?: () => void;
  showFooter?: boolean;
  handleRemove?: (opportunity: Opportunity, index: number) => Promise<void>;
  handleUpdateOpportunities?: (updatedOpportunities: Opportunity[]) => Promise<boolean>;
}

const OpportunitiesStep: React.FC<OpportunitiesStepProps> = ({
  opportunities,
  setOpportunities,
  isEditMode = true,
  onSaveStateChange,
  onSave,
  showFooter = false,
  handleUpdateOpportunities,
  isMobile,
}) => {
  const [localOpportunities, setLocalOpportunities] = useState<Opportunity[]>([]);
  const [initialOpportunities, setInitialOpportunities] = useState<Opportunity[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize
  useEffect(() => {
    const deepCopy = JSON.parse(JSON.stringify(opportunities));
    setLocalOpportunities(deepCopy);
    setInitialOpportunities(deepCopy);
  }, [opportunities]);

  useEffect(() => {
    if (onSaveStateChange) onSaveStateChange(hasChanges);
  }, [hasChanges, onSaveStateChange]);

  // 🔹 Utility: compare states and check if any field has content
  const checkIfHasChanges = (updatedList: Opportunity[]) => {
    const hasMeaningfulInput = updatedList.some(
      (opp) => opp.opportunity_title.trim() !== '' || opp.opportunity_description.trim() !== ''
    );

    const differsFromInitial = JSON.stringify(updatedList) !== JSON.stringify(initialOpportunities);

    // Mark changed only if user has typed or structure differs meaningfully
    setHasChanges(hasMeaningfulInput && differsFromInitial);
  };

  // 🔹 Add new
  const handleAdd = () => {
    const newOpportunity: Opportunity = {
      id: uuid(),
      opportunity_title: '',
      opportunity_description: '',
      affected_process_stages: [],
      expected_business_value: [],
    };
    const updated = [...localOpportunities, newOpportunity];
    setLocalOpportunities(updated);
    setOpportunities(updated);
    checkIfHasChanges(updated);
  };

  // 🔹 Change handler
  const handleChange = (index: number, field: keyof Opportunity, value: string) => {
    const updated = [...localOpportunities];
    updated[index] = { ...updated[index], [field]: value };
    setLocalOpportunities(updated);
    setOpportunities(updated);
    checkIfHasChanges(updated);
  };

  // 🔹 Remove
  const handleRemoveLocal = (index: number) => {
    const updated = localOpportunities.filter((_, i) => i !== index);
    setLocalOpportunities(updated);
    setOpportunities(updated);
    checkIfHasChanges(updated);
  };

  // 🔹 Save
  const handleSave = async () => {
    setOpportunities(localOpportunities);
    try {
      if (handleUpdateOpportunities) {
        await handleUpdateOpportunities(localOpportunities);
      }
      if (onSave) onSave();
      setInitialOpportunities(JSON.parse(JSON.stringify(localOpportunities)));
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving opportunities:', error);
    }
  };

  // 🔹 Discard
  const handleDiscard = () => {
    const copy = JSON.parse(JSON.stringify(initialOpportunities));
    setLocalOpportunities(copy);
    setOpportunities(copy);
    setHasChanges(false);
  };

  return (
    <Box
      sx={{
        p: isMobile ? 2 : 4,
        pt: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '1000px',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {/* <Typography sx={{ fontSize: '24px', fontWeight: 500, color: '#2D3748' }}>
          Proposed Enhancements
        </Typography> */}
        {isEditMode && (
          <Typography sx={{ fontSize: '15px', pt: 3, fontWeight: 400, color: '#1A202C', mb: 3 }}>
            The proposed enhancements will be utilized to generate opportunities for the process
          </Typography>
        )}
        {isEditMode && (
          <Button
            variant="contained"
            onClick={handleAdd}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 15,
              px: 3,
              py: 1,
              borderRadius: '8px',
              backgroundColor: '#2563EB',
              '&:hover': { backgroundColor: '#1D4ED8' },
            }}
          >
            Add
          </Button>
        )}
      </Box>

      {/* {isEditMode && (
        <Typography sx={{ fontSize: '15px', fontWeight: 400, color: '#1A202C', mb: 3 }}>
          The proposed enhancements will be utilized to generate opportunities for the process
        </Typography>
      )} */}

      {localOpportunities.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            border: '1px dashed #E2E8F0',
            borderRadius: '8px',
            backgroundColor: '#F7FAFC',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No proposed enhancements available. Click "Add" to create one.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: '90px' }}>
          {localOpportunities.map((opportunity, index) => (
            <Box key={opportunity.id || index}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1A202C' }}>
                  Proposed Enhancement {index + 1}
                </Typography>
                {isEditMode && (
                  <Button
                    startIcon={
                      <img src="/images/trash-red.svg" alt="remove" width={15} height={15} />
                    }
                    onClick={() => handleRemoveLocal(index)}
                    sx={{
                      textTransform: 'none',
                      color: '#FF0505',
                      fontWeight: 400,
                      fontSize: '15px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              {/* Content */}
              <Box
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  p: 1,
                  backgroundColor: '#FBFBFB',
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', mb: 1 }}>
                    Enhancement
                  </Typography>
                  <TextField
                    fullWidth
                    value={opportunity.opportunity_title}
                    onChange={(e) => handleChange(index, 'opportunity_title', e.target.value)}
                    placeholder="Enter enhancement"
                    disabled={!isEditMode}
                    multiline
                    rows={1}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '16px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': { borderColor: '#D9D9D9' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                      },
                      '& .MuiInputBase-input': { padding: '4px' },
                    }}
                  />
                </Box>

                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#2D3748', mb: 1 }}>
                    Description
                  </Typography>
                  <TextField
                    fullWidth
                    value={opportunity.opportunity_description}
                    onChange={(e) => handleChange(index, 'opportunity_description', e.target.value)}
                    placeholder="Enter description"
                    disabled={!isEditMode}
                    multiline
                    rows={6}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '16px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': { borderColor: '#D9D9D9' },
                        '&:hover fieldset': { borderColor: 'primary.main' },
                        '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                      },
                      '& .MuiInputBase-input': { padding: '4px' },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {showFooter && (
        <FooterActions
          onSave={hasChanges ? handleSave : undefined}
          onDiscard={hasChanges ? handleDiscard : undefined}
          onRegenerate={() => {}}
          saveDisabled={!hasChanges}
          regenerateDisabled={true}
        />
      )}
    </Box>
  );
};

export default OpportunitiesStep;
